import posthog from "posthog-js";

/**
 * Client-side PostHog helpers.
 *
 * PostHog is initialized once in `apps/web/instrumentation-client.ts` (the
 * Next.js 15.3+ convention — never via a React provider). These helpers all
 * operate on that shared browser singleton and no-op safely if PostHog has
 * not been initialized yet (e.g. when a shared `@klarify/ui` component renders
 * in a surface without the instrumentation hook, such as the desktop client).
 *
 * PostHog is deliberately started with `opt_out_capturing_by_default: true`,
 * mirroring the existing Mixpanel consent model — no events (including
 * autocapture) are sent until {@link optInPostHogTracking} is called once the
 * user grants cookie consent.
 */

function isReady(): boolean {
	return posthog.__loaded === true;
}

/**
 * Identify the current user in PostHog and set their person properties.
 * Use the same distinct id (Clerk user id) on the server so client and server
 * events correlate to one person. PII (email, name) belongs here, not in
 * {@link capturePostHogEvent} properties.
 */
export function identifyPostHogUser(
	userId: string,
	properties?: Record<string, unknown>,
): void {
	if (!isReady()) {
		return;
	}
	posthog.identify(userId, properties);
}

/**
 * Capture a client-side product event. The browser SDK automatically attaches
 * the current distinct id, so there is no need to pass a user id.
 */
export function capturePostHogEvent(
	event: string,
	properties?: Record<string, unknown>,
): void {
	if (!isReady()) {
		return;
	}
	posthog.capture(event, properties);
}

/**
 * Report a handled exception to PostHog Error Tracking. Unhandled errors are
 * already captured automatically (`capture_exceptions: true`); use this for
 * errors an error boundary or a `catch` block swallowed, which autocapture
 * never sees.
 */
export function capturePostHogException(
	error: unknown,
	properties?: Record<string, unknown>,
): void {
	if (!isReady()) {
		return;
	}
	posthog.captureException(error, properties);
}

/**
 * Reset PostHog identity (call on sign-out to generate a new anonymous
 * distinct id and unlink future events from the current user).
 */
export function resetPostHogUser(): void {
	if (!isReady()) {
		return;
	}
	posthog.reset();
}

/**
 * Opt the current user in to PostHog capturing (call once cookie consent is
 * granted). No-ops if already opted in.
 */
export function optInPostHogTracking(): void {
	if (!isReady()) {
		return;
	}
	if (posthog.has_opted_out_capturing()) {
		posthog.opt_in_capturing();
	}
}

/**
 * Opt the current user out of PostHog capturing (call on cookie-consent
 * rejection). No-ops if already opted out.
 */
export function optOutPostHogTracking(): void {
	if (!isReady()) {
		return;
	}
	if (!posthog.has_opted_out_capturing()) {
		posthog.opt_out_capturing();
	}
}

/**
 * Associate the current user with their clinic as a PostHog group, so events
 * roll up to the clinic. This group is for analytics only — it never reaches
 * flag evaluation, because client-side flags are disabled
 * (`advanced_disable_feature_flags`) and the server passes person properties,
 * never groups.
 *
 * So clinic-targeted flags must condition on the `clinic_id` **person**
 * property, which flagsServer supplies as a runtime override. A flag
 * conditioned on a *group* property has no server-side equivalent: local
 * evaluation raises InconclusiveMatchError, which makes posthog-node fall
 * back to a billable remote call for the whole batch — and that call can't
 * resolve it either, since we send no `groups` argument.
 * No-ops until PostHog is initialized.
 */
export function groupPostHogClinic(
	clinicId: string,
	properties: { name?: string; plan?: string },
): void {
	if (!isReady()) {
		return;
	}
	posthog.group("clinic", clinicId, properties);
}

/**
 * Deep link to the current session's replay in PostHog. Stored against recorded
 * sessions (via the tRPC replay-id header) so support can jump straight to the
 * replay. Returns an empty string until PostHog is initialized and a recording
 * is active — callers send it as-is, and the server treats an empty value as
 * "no replay".
 */
export function getPostHogSessionReplayUrl(): string {
	if (!isReady()) {
		return "";
	}
	return posthog.get_session_replay_url({ withTimestamp: true }) ?? "";
}
